import {config} from "@/constants/config";
import {useAuthStore} from "@/store/auth.store";
import {useAuthStore as useLegacyAuthStore} from "@/store/useAuth.store";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface HttpRequestOptions<TBody = unknown> {
  method?: HttpMethod;
  url: string;
  baseUrl?: string;
  body?: TBody;
  query?: Record<string, string | number | boolean | null | undefined>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  auth?: boolean;
}

export class HttpError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.data = data;
  }
}

const buildUrl = (baseUrl: string, url: string, query?: HttpRequestOptions["query"]): string => {
  const root = baseUrl.replace(/\/$/, "");
  const path = url.startsWith("/") ? url : `/${url}`;
  const full = `${root}${path}`;
  if (!query) return full;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined) continue;
    params.append(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${full}?${qs}` : full;
};

export const httpRequest = async <TResponse = unknown, TBody = unknown>(
  options: HttpRequestOptions<TBody>
): Promise<TResponse> => {
  const {method = "GET", url, baseUrl = config.apiBaseUrl, body, query, headers = {}, signal, auth = true} = options;

  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...headers,
  };

  if (body !== undefined && !(body instanceof FormData)) {
    finalHeaders["Content-Type"] ??= "application/json";
  }

  if (auth) {
    const token = useAuthStore.getState().token ?? useLegacyAuthStore.getState().token;
    if (token) finalHeaders.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(buildUrl(baseUrl, url, query), {
    method,
    headers: finalHeaders,
    body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
    signal,
  });

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload: unknown = isJson ? await response.json().catch(() => null) : await response.text();

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      useAuthStore.getState().clearSession();
    }
    const message =
      (isJson && payload && typeof payload === "object" && "message" in payload
        ? String((payload as {message?: unknown}).message)
        : null) ?? `HTTP ${response.status}`;
    throw new HttpError(message, response.status, payload);
  }

  return payload as TResponse;
};
